import { useState, useCallback } from 'react';

/**
 * Image state containing base64 data, mime type, filename, and dimensions
 */
interface ImageState {
  data: string; // base64 encoded image data (without data URL prefix)
  mimeType: string;
  name: string;
  dimensions: {
    width: number;
    height: number;
  };
}

/**
 * Analysis result from the image analysis API
 */
interface ImageAnalysisResult {
  contentType: {
    primary: string;
    confidence: number;
  };
  palette: string[]; // hex colors
  prompt: string;
}

/**
 * Return type for useImageAnalysis hook
 */
interface UseImageAnalysisReturn {
  // Image state
  image: ImageState | null;
  setImage: (file: File | null) => void;
  clearImage: () => void;

  // Analysis state
  analyze: (preferences?: {
    fps?: number;
    width?: number;
    height?: number;
    durationInSeconds?: number;
  }) => Promise<void>;
  isAnalyzing: boolean;
  analysis: ImageAnalysisResult | null;
  error: string | null;
  clearAnalysis: () => void;
}

// Valid image MIME types
const VALID_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// Maximum file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Hook for managing image upload and analysis
 *
 * Features:
 * - File validation (type, size)
 * - Base64 conversion
 * - Dimension detection
 * - API integration for vision analysis
 */
export function useImageAnalysis(): UseImageAnalysisReturn {
  const [image, setImageState] = useState<ImageState | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<ImageAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Set image from a File object
   * Validates file type and size, converts to base64, and extracts dimensions
   */
  const setImage = useCallback((file: File | null) => {
    // Handle null/clear case
    if (!file) {
      setImageState(null);
      return;
    }

    // Validate file type
    if (!VALID_MIME_TYPES.includes(file.type)) {
      setError('Please upload a PNG, JPEG, or WebP image');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > MAX_FILE_SIZE) {
      setError('Image must be less than 10MB');
      return;
    }

    // Read file as base64
    const reader = new FileReader();

    reader.onerror = () => {
      setError('Failed to read image file');
    };

    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) {
        setError('Failed to read image file');
        return;
      }

      // Extract base64 data (remove data URL prefix)
      const base64 = dataUrl.split(',')[1];

      // Create image element to get dimensions
      const img = new Image();

      img.onerror = () => {
        setError('Failed to load image');
      };

      img.onload = () => {
        setImageState({
          data: base64,
          mimeType: file.type,
          name: file.name,
          dimensions: {
            width: img.width,
            height: img.height,
          },
        });
        // Clear any previous error and analysis when new image is set
        setError(null);
        setAnalysis(null);
      };

      img.src = dataUrl;
    };

    reader.readAsDataURL(file);
  }, []);

  /**
   * Clear image and reset all state
   */
  const clearImage = useCallback(() => {
    setImageState(null);
    setAnalysis(null);
    setError(null);
  }, []);

  /**
   * Analyze the current image using the vision API
   * @param preferences - Optional video preferences (fps, width, height, durationInSeconds)
   */
  const analyze = useCallback(async (preferences?: {
    fps?: number;
    width?: number;
    height?: number;
    durationInSeconds?: number;
  }) => {
    if (!image) {
      setError('No image to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const response = await fetch('/api/analyze-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: image.data,
          mimeType: image.mimeType,
          preferences,
        }),
      });

      const data = await response.json();

      // Response format: { success: true, data: { analysis: {...}, tokensUsed: number } }
      if (data.success && data.data?.analysis) {
        setAnalysis({
          contentType: data.data.analysis.contentType,
          palette: data.data.analysis.palette,
          prompt: data.data.analysis.prompt,
        });
      } else {
        // Handle error object format: { code: string, message: string }
        const errorMessage =
          typeof data.error === 'string'
            ? data.error
            : data.error?.message || 'Image analysis failed';
        setError(errorMessage);
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setIsAnalyzing(false);
    }
  }, [image]);

  /**
   * Clear only the analysis result, keep the image
   */
  const clearAnalysis = useCallback(() => {
    setAnalysis(null);
    setError(null);
  }, []);

  return {
    image,
    setImage,
    clearImage,
    analyze,
    isAnalyzing,
    analysis,
    error,
    clearAnalysis,
  };
}
